import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider } from "styled-components";
import GlobalStyles from "./styles/GlobalStyles";
import theme from "./styles/theme";
import Container from "./components/UI/Container";
import PromptForm from "./components/PromptForm/PromptForm";
import AuthPage from "./components/Auth/AuthPage";
import ScriptsPage from "./components/Scripts/ScriptsPage";
import Dashboard from "./components/Dashboard/Dashboard";
import PrivateRoute from "./components/Auth/PrivateRoute";
import Footer from "./components/Layout/Footer";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import styled, { keyframes } from "styled-components";
import { supabase } from "./utils/supabase";

const glow = keyframes`
  0% {text-shadow: 0 0 5px rgba(255, 215, 0, 0.3), 0 0 10px rgba(255, 215, 0, 0.2), 0 0 15px rgba(255, 215, 0, 0.1);}
  50% {text-shadow: 0 0 10px rgba(255, 215, 0, 0.5), 0 0 20px rgba(255, 215, 0, 0.3), 0 0 30px rgba(255, 215, 0, 0.2);}
  100% {text-shadow: 0 0 5px rgba(255, 215, 0, 0.3), 0 0 10px rgba(255, 215, 0, 0.2), 0 0 15px rgba(255, 215, 0, 0.1);}
`;

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 4rem 0 0 0;
  background: radial-gradient(ellipse at center, #131313 0%, #000000 80%);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23d4af37' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E");
    opacity: 0.3;
    z-index: 0;
  }

  @media (max-width: 992px) {
    padding: 3rem 0 0 0;
  }

  @media (max-width: 768px) {
    padding: 2rem 0 0 0;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 0 0 0;
  }
`;

const LogoShine = keyframes`
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
  z-index: 1;

  &::after {
    content: "";
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;
    height: 2px;
    background: linear-gradient(
      90deg,
      rgba(212, 175, 55, 0) 0%,
      rgba(212, 175, 55, 1) 50%,
      rgba(212, 175, 55, 0) 100%
    );
  }

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
  }

  h1 {
    font-family: "Playfair Display", serif;
    font-size: 3.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    position: relative;
    background: linear-gradient(
      90deg,
      #bf953f,
      #fcf6ba,
      #b38728,
      #fbf5b7,
      #aa771c
    );
    background-size: 200% auto;
    color: transparent;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: ${LogoShine} 5s linear infinite;
    letter-spacing: 2px;

    &::before,
    &::after {
      content: "";
      position: absolute;
      width: 40px;
      height: 2px;
      background: linear-gradient(
        90deg,
        rgba(212, 175, 55, 0) 0%,
        rgba(212, 175, 55, 1) 100%
      );
      top: 50%;
    }

    &::before {
      left: -60px;
      transform: translateX(-100%);
    }

    &::after {
      right: -60px;
      transform: translateX(100%);
    }

    @media (max-width: 768px) {
      font-size: 2.5rem;
      &::before,
      &::after {
        display: none;
      }
    }

    @media (max-width: 480px) {
      font-size: 2rem;
      margin-bottom: 0.5rem;
    }
  }

  p {
    font-family: "Cormorant Garamond", serif;
    color: var(--text-secondary);
    font-size: 1.3rem;
    max-width: 600px;
    margin: 0 auto;
    font-weight: 300;
    letter-spacing: 1px;
    animation: ${glow} 4s ease-in-out infinite;

    @media (max-width: 768px) {
      font-size: 1.1rem;
    }

    @media (max-width: 480px) {
      font-size: 1rem;
      max-width: 320px;
    }
  }
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
`;

// Auth callback handler component
const AuthCallbackHandler = () => {
  useEffect(() => {
    // Handle the OAuth callback
    const { hash } = window.location;
    console.log("Auth callback handling initiated, URL:", window.location.href);

    // Supabase will handle access token in both hash and query params
    if (
      (hash && hash.includes("access_token")) ||
      window.location.search.includes("code=")
    ) {
      console.log("Auth tokens detected in URL");

      // Tell Supabase to handle the callback
      supabase.auth.getSession().then(({ data, error }) => {
        if (error) {
          console.error("Auth callback error:", error);
          return;
        }

        console.log(
          "Auth callback: Session established",
          data?.session ? "Successfully" : "Failed"
        );

        // Get the hostname to determine appropriate redirect
        const currentHostname = window.location.hostname;
        let redirectPath;

        if (
          currentHostname === "localhost" ||
          currentHostname === "127.0.0.1"
        ) {
          redirectPath = "/scripts";
        } else {
          // Use full URL for production environments
          redirectPath = `${window.location.origin}/scripts`;
        }

        console.log("Redirecting to:", redirectPath);

        // Redirect to the scripts page after successful authentication
        window.location.replace(redirectPath);
      });
    } else {
      console.log("No auth tokens found in URL");
    }
  }, []);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <h2 style={{ fontFamily: "Playfair Display, serif" }}>
          Logging you in...
        </h2>
        <p>Please wait while we complete the authentication process.</p>
      </div>
    </div>
  );
};

// HomePage wrapper to check authentication
const ProtectedHomePage = () => {
  const { currentUser } = useAuth();

  if (!currentUser) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" replace />;
  }

  return <Dashboard />;
};

function App() {
  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800;900&family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  // Check for redirect URL from 404 page
  useEffect(() => {
    const redirectUrl = sessionStorage.getItem("redirectUrl");

    if (redirectUrl) {
      // Clear the stored URL to prevent future redirects
      sessionStorage.removeItem("redirectUrl");

      // Use history to navigate without a page refresh
      if (window.location.pathname !== redirectUrl) {
        window.history.replaceState(null, "", redirectUrl);
      }
    }
  }, []);

  return (
    <Router>
      <AuthProvider>
        <ThemeProvider theme={theme}>
          <GlobalStyles />
          <AppContainer>
            <MainContent>
              <Routes>
                {/* Home Page - now uses Dashboard component */}
                <Route path="/" element={<ProtectedHomePage />} />

                {/* Auth Pages */}
                <Route path="/login" element={<AuthPage />} />
                <Route
                  path="/auth/callback"
                  element={<AuthCallbackHandler />}
                />

                {/* Protected Routes */}
                <Route element={<PrivateRoute />}>
                  {/* Scripts page now redirects to homepage since they're combined */}
                  <Route
                    path="/scripts"
                    element={<Navigate to="/" replace />}
                  />
                </Route>

                {/* Fallback Route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </MainContent>
            <Footer />
          </AppContainer>
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
